<template>
  <div class="col-12">
    <div class="card mb-5 " style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t("gradeSheets.editTitle") }}
            </h6>
          </div>
        </div>
      </div>

      <LoadingSpinner v-if="isLoading" :message="$t('gradeSheets.loading')" />
      
      <div v-else class="card-body px-0 mx-2">
        <div class="row justify-content-center">
          <div class="col-md-8">
            <GradeSheetForm
              ref="gradeSheetForm"
              :initial-data="gradeSheet"
              :is-edit="true"
              @submit="updateGradeSheet"
              @cancel="navigateBack"
              @validation-error="handleValidationError"
              @update:form-data="handleFormUpdate"
            />
            
            <div class="d-flex gap-3 mt-3">
              <button
                type="button"
                class="btn btn-primary btn-sm flex-grow-1"
                @click="submit"
                :disabled="isSubmitting || !hasChanges"
              >
                <span v-if="isSubmitting">
                  <i class="fas fa-spinner fa-spin ms-2"></i>
                  {{ $t("common.saving") }}
                </span>
                <span v-else>
                  <i class="fas fa-save ms-2"></i>
                  {{ $t("gradeSheets.saveChanges") }}
                </span>
              </button>
              
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm flex-grow-1"
                @click="navigateBack"
                :disabled="isSubmitting"
              >
                <i class="fas fa-times ms-2"></i>
                {{ $t("common.cancel") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import GradeSheetForm from "@/components/features/grade-sheets/GradeSheetForm.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { useToast } from "vue-toastification";

import { useI18n } from "vue-i18n";
const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const store = useStore();
const toast = useToast();

const isLoading = ref(false);
const isSubmitting = ref(false);
const gradeSheetForm = ref(null);
const originalGradeSheet = ref(null);
const currentFormData = ref(null);

const hasChanges = computed(() => {
  if (!originalGradeSheet.value || !currentFormData.value) return false;
  
  return Object.keys(currentFormData.value).some(key => {
    if (key === 'created_at' || key === 'updated_at') return false;
    
    const currentValue = currentFormData.value[key];
    const originalValue = originalGradeSheet.value[key];
    
    if (key === 'structures') {
      if (currentValue.length !== originalValue.length) return true;
      
      for (let i = 0; i < currentValue.length; i++) {
        const currentStruct = currentValue[i];
        const originalStruct = originalValue[i];
        
        if (
          currentStruct.column_name !== originalStruct.column_name ||
          currentStruct.full_degree !== originalStruct.full_degree ||
          currentStruct.column_color !== originalStruct.column_color
        ) {
          return true;
        }
      }
      return false;
    }
    
    return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
  });
});

const handleFormUpdate = (newData) => {
  currentFormData.value = { ...newData };
};

const gradeSheet = computed(() => {
  const gs = store.getters['gradeSheets/getCurrentGradeSheet'];
  
  if (!gs) {
    return {
      title: '',
      class_id: '',
      subject_id: '',
      structures: [
        { column_name: '', full_degree: '', column_color: '#3A416F' }
      ]
    };
  }
  
  const structures = Array.isArray(gs.structures) ? gs.structures : [];
  
  return {
    title: gs.title || '',
    class_id: gs.class?.id || '', 
      class_group_id: gs.group?.id || '', 
    subject_id: gs.subject?.id || '',  
    structures: structures.map(s => ({
      id: s.id || null,
      column_name: s.column_name || '',
      full_degree: s.full_degree || '',
      column_color: s.column_color || '#3A416F'
    }))
  };
});

const fetchGradeSheet = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('gradeSheets/fetchGradeSheet', route.params.id);
    
    originalGradeSheet.value = JSON.parse(JSON.stringify(gradeSheet.value));
    currentFormData.value = JSON.parse(JSON.stringify(gradeSheet.value));
    
    if (gradeSheetForm.value) {
      gradeSheetForm.value.formData = { ...gradeSheet.value };
      
      if (gradeSheet.value.class_id) {
        await gradeSheetForm.value.fetchClassSubjects();
        gradeSheetForm.value.formData.subject_id = gradeSheet.value.subject_id;
      }
    }
  } catch (error) {
    toast.error(t("gradeSheets.loadError"));
    console.error("Error fetching grade sheet:", error);
    router.push({ name: 'grade-sheets.index' });
  } finally {
    isLoading.value = false;
  }
};
const submit = () => {
  if (gradeSheetForm.value) {
    gradeSheetForm.value.submit();
  }
};

const updateGradeSheet = async (formData) => {
  if (!hasChanges.value) {
    toast.info(t("common.noChanges"));
    return;
  }

  isSubmitting.value = true;
  try {
    await store.dispatch('gradeSheets/updateGradeSheet', {
      gradeSheetId: route.params.id,
      gradeSheetData: formData
    });
    toast.success(t("gradeSheets.updateSuccess"));
    router.push({ name: 'grade-sheets.index' });
  } catch (error) {
    if (error.response?.status === 422) {
      toast.error(t("common.validationError"));
    } else {
      toast.error(error.response?.data?.message || t("gradeSheets.updateError"));
    }
    console.error("Error updating grade sheet:", error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleValidationError = () => {
  toast.warning(t("common.fillRequired"));
};

const navigateBack = () => {
  router.push({ name: 'grade-sheets.index' });
};

onMounted(() => {
  fetchGradeSheet();
});
</script>
